import { type FC, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useLanguage } from "src/hooks/UseLanguage";
import { mirrorChartMargin, rtlChartProps } from "src/components/charts/ChartRtl";
import type { SalesAttributionChannelComparisonRow } from "../utils/SalesAttributionTypes";
import {
  AXIS_TICK,
  CHART_CHROME,
  LEGEND_PROPS,
  SOURCE_COLORS,
  localizeChartDigits,
} from "./SalesAttributionShared";

const TREND_CHART_MARGIN = { top: 8, right: 8, bottom: 0, left: -8 };

const SERIES_LABEL_DEFAULT: Record<string, string> = {
  shopify: "Shopify booked",
  ga4: "GA4 measured",
};

const seriesLabel = (t: TFunction, key: string): string =>
  t(`salesAttribution.ui.channelBars.${key === "shopify" ? "shopifyBooked" : "ga4Measured"}`, {
    defaultValue: SERIES_LABEL_DEFAULT[key] ?? key,
  });

const CHANNEL_SERIES_COLORS: Record<string, string> = {
  shopify: SOURCE_COLORS.shopify,
  ga4: SOURCE_COLORS.ga4,
};

interface ChannelRevenueDatum {
  channel: string;
  shopify: number;
  ga4: number;
  shopifyFormatted: string;
  ga4Formatted: string;
}

const TooltipSeriesDot: FC<{ color: string }> = ({ color }) => (
  <svg width={8} height={8} viewBox="0 0 8 8" aria-hidden="true">
    <circle cx={4} cy={4} r={4} fill={color} />
  </svg>
);

interface ChannelRevenueTooltipProps {
  active?: boolean;
  label?: string;
  payload?: Array<{
    name?: string;
    dataKey?: string | number;
    color?: string;
    payload?: ChannelRevenueDatum;
  }>;
}

const ChannelRevenueTooltip: FC<ChannelRevenueTooltipProps> = ({
  active,
  label,
  payload,
}) => {
  const { t } = useTranslation();

  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="sales-attribution__chart-tooltip">
      <p className="sales-attribution__chart-tooltip-title">{label}</p>
      <ul className="sales-attribution__chart-tooltip-list">
        {payload.map(entry => {
          const key = String(entry.dataKey ?? "");
          const datum = entry.payload;
          const formatted =
            key === "ga4" ? datum?.ga4Formatted : datum?.shopifyFormatted;

          return (
            <li key={key} className="sales-attribution__chart-tooltip-row">
              <span className="sales-attribution__chart-tooltip-label sales-attribution__chart-tooltip-label--with-dot">
                <TooltipSeriesDot
                  color={entry.color ?? CHANNEL_SERIES_COLORS[key] ?? SOURCE_COLORS.shopify}
                />
                {entry.name ?? seriesLabel(t, key)}
              </span>
              <span className="sales-attribution__chart-tooltip-value">
                {formatted ?? "—"}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

interface ChannelRevenueBarsProps {
  rows: SalesAttributionChannelComparisonRow[];
}

export const ChannelRevenueBars: FC<ChannelRevenueBarsProps> = ({ rows }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const chartRtl = rtlChartProps(isRTL);
  const data = useMemo<ChannelRevenueDatum[]>(
    () =>
      rows.map(row => ({
        channel: row.channelName,
        shopify: row.shopifyRevenue.value,
        ga4: row.ga4Revenue.value,
        shopifyFormatted: row.shopifyRevenue.formattedValue,
        ga4Formatted: row.ga4Revenue.formattedValue,
      })),
    [rows]
  );

  if (data.length === 0) {
    return (
      <p className="sales-attribution__empty">
        {t("salesAttribution.ui.channelBars.emptyState", {
          defaultValue:
            "No channel was reported with revenue by both Shopify and GA4 for the selected period.",
        })}
      </p>
    );
  }

  return (
    <div
      className="sales-attribution__chart"
      role="img"
      aria-label={t("salesAttribution.ui.channelBars.aria", {
        defaultValue: "Shopify versus GA4 revenue by channel",
      })}
    >
      <ResponsiveContainer width="100%" height={360}>
        <BarChart data={data} margin={mirrorChartMargin(TREND_CHART_MARGIN, isRTL)} barGap={2}>
          <CartesianGrid stroke={CHART_CHROME.grid} vertical={false} horizontal={false} />
          <XAxis
            dataKey="channel"
            reversed={isRTL}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: CHART_CHROME.axis }}
            interval={0}
            angle={-20}
            textAnchor="end"
            height={70}
          />

          <YAxis
            orientation={chartRtl.yAxisOrientation}
            tickFormatter={value => localizeChartDigits(Number(value).toLocaleString(), isRTL)}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={82}
          />
          <Tooltip
            cursor={{ fill: CHART_CHROME.cursor }}
            content={<ChannelRevenueTooltip />}
          />
          <Legend {...LEGEND_PROPS} />
          <Bar
            dataKey="shopify"
            name={seriesLabel(t, "shopify")}
            fill={SOURCE_COLORS.shopify}
            fillOpacity={0.85}
            radius={[3, 3, 0, 0]}
            maxBarSize={26}
          />
          <Bar
            dataKey="ga4"
            name={seriesLabel(t, "ga4")}
            fill={SOURCE_COLORS.ga4}
            fillOpacity={0.85}
            radius={[3, 3, 0, 0]}
            maxBarSize={26}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ChannelRevenueBars;
